import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  FaSpinner, FaArrowLeft, FaClock, FaCheck, FaExclamationTriangle, 
  FaEnvelope, FaUserPlus, FaFileAlt, FaClipboardList, FaShippingFast, 
  FaThumbsUp, FaCommentDots, FaPhone, FaCalendarAlt, FaUser
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { verifyMagicLink, getQuotationByMagicLink } from '../services/quotationAuthService';
import '../styles/timeline.css';

// Mapeo de estados a colores
const STATUS_COLORS = {
  pending: 'warning',
  en_proceso: 'info',
  estimado_listo: 'info',
  aprobada: 'success',
  completada: 'success',
  rechazada: 'danger'
};

// Mapeo de estados a etiquetas
const STATUS_LABELS = {
  pending: 'Pendiente',
  en_proceso: 'En Proceso',
  estimado_listo: 'Estimado Listo',
  aprobada: 'Aprobada',
  completada: 'Completada',
  rechazada: 'Rechazada'
};

// Mapeo de estados a iconos
const STATUS_ICONS = {
  pending: FaClipboardList,
  en_proceso: FaFileAlt,
  estimado_listo: FaCheck,
  aprobada: FaThumbsUp,
  completada: FaShippingFast,
  rechazada: FaExclamationTriangle
};

// Componente de línea de tiempo para mostrar el progreso
const QuotationTimeline = ({ status }) => {
  const getStatusClass = (checkStatus) => {
    if (status === 'rechazada' && checkStatus !== 'rechazada') {
      return 'timeline-step-inactive';
    }
    
    const statusOrder = ['pending', 'en_proceso', 'estimado_listo', 'aprobada', 'completada'];
    const currentIndex = statusOrder.indexOf(status);
    const checkIndex = statusOrder.indexOf(checkStatus);
    
    if (currentIndex >= checkIndex) {
      return 'timeline-step-active';
    }
    
    return 'timeline-step-inactive';
  };
  
  // Función para obtener el icono según el estado
  const getStatusIcon = (checkStatus) => {
    const IconComponent = STATUS_ICONS[checkStatus] || FaCalendarAlt;
    return <IconComponent />;
  };
  
  return (
    <div className="quotation-timeline">
      <div className="timeline-container">
        <div className={`timeline-step ${getStatusClass('pending')}`}>
          <div className="timeline-icon">
            {getStatusIcon('pending')}
          </div>
          <div className="timeline-content">
            <h5>Solicitud Recibida</h5>
            <p>Hemos recibido tu solicitud de cotización</p>
          </div>
        </div>
        
        <div className={`timeline-step ${getStatusClass('en_proceso')}`}>
          <div className="timeline-icon">
            {getStatusIcon('en_proceso')}
          </div>
          <div className="timeline-content">
            <h5>En Proceso</h5>
            <p>Estamos preparando tu cotización</p>
          </div>
        </div>
        
        <div className={`timeline-step ${getStatusClass('estimado_listo')}`}>
          <div className="timeline-icon">
            {getStatusIcon('estimado_listo')}
          </div>
          <div className="timeline-content">
            <h5>Estimado Listo</h5>
            <p>Tu estimado está listo para revisión</p>
          </div>
        </div>
        
        <div className={`timeline-step ${getStatusClass('completada')}`}>
          <div className="timeline-icon">
            {getStatusIcon('completada')}
          </div>
          <div className="timeline-content">
            <h5>Proceso Completado</h5>
            <p>Tu cotización ha sido finalizada</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function QuotationTrackingPage() {
  const { magicLink } = useParams();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verification, setVerification] = useState(null);
  const [isConverted, setIsConverted] = useState(false);

  // Obtener los detalles de la cotización usando el magic link
  useEffect(() => {
    const fetchQuotationDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Verificando magic link para seguimiento:', magicLink);
        
        // Primero verificar si el magic link es válido
        const verificationResult = await verifyMagicLink(magicLink);
        setVerification(verificationResult);
        
        // Verificar si la cotización ya está asociada a una cuenta
        setIsConverted(verificationResult.isConverted || false);
        
        if (!verificationResult.isValid) {
          throw new Error('El enlace de seguimiento no es válido o ha expirado');
        }
        
        // Si es válido, obtener los detalles de la cotización
        const result = await getQuotationByMagicLink(magicLink);
        setQuotation(result);
        
        console.log('Cotización cargada para seguimiento:', {
          id: result.id,
          status: result.get('status'),
          createdAt: result.get('createdAt')
        });
      } catch (err) {
        console.error('Error al cargar cotización para seguimiento:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (magicLink) {
      fetchQuotationDetails();
    }
  }, [magicLink]);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <FaSpinner className="fa-spin mb-3" size={30} />
        <h4>Cargando detalles de la cotización...</h4>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          <h4 className="alert-heading">Error al cargar la cotización</h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">
            <Link to="/" className="btn btn-outline-danger">
              <FaArrowLeft className="me-1" /> Volver al inicio
            </Link>
          </p>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          <h4 className="alert-heading">Cotización no encontrada</h4>
          <p>No se pudo encontrar la cotización solicitada.</p>
          <hr />
          <p className="mb-0">
            <Link to="/" className="btn btn-outline-warning">
              <FaArrowLeft className="me-1" /> Volver al inicio
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Extraer información relevante de la cotización
  const status = quotation.get('status') || 'pending';
  const createdAt = quotation.get('createdAt');
  const formattedDate = createdAt ? new Date(createdAt).toLocaleDateString() : 'Fecha no disponible';
  const userEmail = quotation.get('userEmail') || '';
  const userName = quotation.get('userName') || '';
  const productType = quotation.get('productType') || 'No especificado';
  const customFormula = quotation.get('customFormula') || '';
  const additionalServices = quotation.get('additionalServices') || [];
  const items = quotation.get('items') || [];
  const notes = quotation.get('notes') || '';

  return (
    <div className="container py-4">
      <div className="row mb-4">
        <div className="col">
          <h2 className="mb-3">Seguimiento de Cotización</h2>
          <p className="text-muted">
            Consulta el estado actual de tu cotización y su progreso.
          </p>
        </div>
      </div>

      {/* Estado actual */}
      <div className="card mb-4 border-0 shadow-sm">
        <div className="card-body">
          <div className="d-flex align-items-center mb-3">
            <div className={`status-badge bg-${STATUS_COLORS[status]} me-3`}>
              {STATUS_ICONS[status] && React.createElement(STATUS_ICONS[status], { size: 18 })}
            </div>
            <div>
              <h5 className="mb-0">Estado: {STATUS_LABELS[status]}</h5>
              <small className="text-muted">Actualizado: {formattedDate}</small>
            </div>
          </div>
          
          {/* Línea de tiempo */}
          <QuotationTimeline status={status} />
          
          {/* Información básica */}
          <div className="row mt-4">
            <div className="col-md-6">
              <div className="d-flex align-items-center">
                <FaEnvelope className="text-primary me-2" />
                <div>
                  <small className="text-muted d-block">Email</small>
                  <strong>{userEmail}</strong>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-center">
                <FaUser className="text-primary me-2" />
                <div>
                  <small className="text-muted d-block">Nombre</small>
                  <strong>{userName}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
          
      {/* Mostrar información del producto */}
      <div className="card mb-4 border-0 shadow-sm">
        <div className="card-body">
          <h5>Productos Solicitados</h5>
          
          {customFormula && (
            <div className="mb-4">
              <h6>Fórmula Personalizada:</h6>
              <p className="bg-light p-3 rounded">{customFormula}</p>
            </div>
          )}
          
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>SKU</th>
                  <th>Pastillas/Frasco</th>
                  <th>Cantidad</th>
                  <th>Precio</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.productName || productType || 'No especificado'}</td>
                      <td>{item.sku || 'WW200-398-060'}</td>
                      <td>{item.pillsPerBottle || 60}</td>
                      <td>{item.quantity || 200}</td>
                      <td>{item.unitPrice ? `$${item.unitPrice}` : 'A cotizar'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td>{productType || 'No especificado'}</td>
                    <td>WW200-398-060</td>
                    <td>60</td>
                    <td>200</td>
                    <td>A cotizar</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
          
      {/* Notas adicionales */}
      {notes && (
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-body">
            <h5>Notas Adicionales</h5>
            <p>{notes}</p>
          </div>
        </div>
      )}
      
      {/* Servicios adicionales */}
      {additionalServices && additionalServices.length > 0 && (
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-body">
            <h5>Servicios Adicionales</h5>
            <ul className="list-group list-group-flush">
              {additionalServices.map((service, index) => (
                <li key={index} className="list-group-item bg-transparent">
                  <FaCheck className="text-success me-2" /> {service}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      
      {/* Acciones disponibles */}
      <div className="card mt-4 bg-light border-0">
        <div className="card-body">
          <h5 className="mb-3">¿Necesitas ayuda?</h5>
          <div className="d-flex align-items-center mb-3">
            <FaCommentDots className="text-primary me-2" size={20} />
            <div>
              <p className="mb-0">Si tienes alguna pregunta sobre tu cotización, no dudes en contactarnos.</p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-3">
            {/* Contactar soporte */}
            <a 
              href={`mailto:info@vvhealth.com?subject=Consulta%20sobre%20cotización%20${quotation.id}`}
              className="btn btn-outline-primary"
            >
              <FaEnvelope className="me-1" /> Enviar Email
            </a>
            
            <a href="tel:+17873547839" className="btn btn-outline-primary">
              <FaPhone className="me-1" /> Llamar
            </a>
            
            {/* Ver detalles completos */}
            <Link to={`/quotation/detail/${magicLink}`} className="btn btn-primary">
              <FaFileAlt className="me-1" /> Ver Detalles Completos
            </Link>
            
            {/* Crear cuenta si no está convertida y no está autenticado */}
            {!isConverted && !isAuthenticated && (
              <Link to={`/quotation/auth/${magicLink}`} className="btn btn-success ms-auto">
                <FaUserPlus className="me-1" />
                Crear Cuenta
              </Link>
            )}
            
            {/* Volver al inicio o al dashboard */}
            {isAuthenticated ? (
              <Link to="/dashboard/quotations" className="btn btn-outline-secondary">
                <FaArrowLeft className="me-1" />
                Volver a mis cotizaciones
              </Link>
            ) : (
              <Link to="/" className="btn btn-outline-secondary">
                <FaArrowLeft className="me-1" />
                Volver al inicio
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
